import React, { useEffect, useState, useRef, useCallback } from "react";
import { Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import "./AdminShared.css";
import { API_BASE_URL } from "../config";

const PARTNER_IMAGE_API = `${API_BASE_URL}/admin/images`;

const ImageUploadManager = () => {
  const navigate = useNavigate();
  const [uploadedImages, setUploadedImages] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchImages = useCallback(async (token) => {
    try {
      setLoading(true);
      const res = await fetch(PARTNER_IMAGE_API, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setUploadedImages(
        Array.isArray(data)
          ? data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          : [],
      );
    } catch {
      // Error logged silently in this node
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const adminInfo = localStorage.getItem("adminInfo");
    if (!adminInfo) return navigate("/admin");
    try {
      const { token } = JSON.parse(adminInfo);
      const decoded = jwtDecode(token);
      if (decoded.exp * 1000 < Date.now()) {
        localStorage.removeItem("adminInfo");
        return navigate("/admin");
      }
      fetchImages(token);
    } catch {
      localStorage.removeItem("adminInfo");
      navigate("/admin");
    }
  }, [fetchImages, navigate]);

  const handleFileSelect = (file) => {
    if (file && file.type.startsWith("image/")) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      alert("Invalid asset type.");
    }
  };

  const handleFileChange = (e) => handleFileSelect(e.target.files[0]);

  const saveImageToDatabase = async () => {
    if (!selectedFile) return;
    const token = JSON.parse(localStorage.getItem("adminInfo"))?.token;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const res = await fetch(`${PARTNER_IMAGE_API}/upload`, {
        method: "POST",
        body: formData,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Upload failed");
      const newImage = await res.json();
      setUploadedImages((prev) => [newImage, ...prev]);
      setSelectedFile(null);
      setPreviewUrl(null);
      alert("✅ Partner Asset Deployed.");
    } catch {
      alert("Deployment failed.");
    } finally {
      setLoading(false);
    }
  };

  const deleteImage = async (id) => {
    const token = JSON.parse(localStorage.getItem("adminInfo"))?.token;
    if (!window.confirm("Purge this stakeholder asset?")) return;
    try {
      const res = await fetch(`${PARTNER_IMAGE_API}/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Purge failed");
      setUploadedImages((prev) => prev.filter((img) => img._id !== id));
    } catch {
      alert("Purge failed.");
    }
  };

  if (loading && uploadedImages.length === 0)
    return (
      <div className="admin-list-container">
        <p style={{ padding: "100px", textAlign: "center" }}>
          ⏳ Synchronizing Partner Assets...
        </p>
      </div>
    );

  return (
    <div className="admin-list-container">
      <div className="admin-list-content">
        <div className="page-title-section">
          <h1>Stakeholder Media</h1>
          <p>Manage and synchronize corporate partner assets.</p>
        </div>
        <div className="admin-info-bar">
          <div className="admin-user-tag">
            <div className="dot" style={{ background: "#ec4899" }}></div>
            Operator: Ecosystem Relations Node
          </div>
          <div
            className="admin-status-badge"
            style={{
              background: "#fdf2f8",
              color: "#9d174d",
              fontSize: "0.75rem",
              fontWeight: 800,
            }}
          >
            {uploadedImages.length} REGISTERED PARTNERS
          </div>
        </div>

        <div
          className="admin-table-wrapper"
          style={{ padding: "40px", background: "#fff" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "30px",
            }}
          >
            <h3 style={{ color: "#0f172a", fontWeight: "800" }}>
              Operational Ecosystem Partners
            </h3>
            <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: "none" }}
                id="partner-upload-input"
              />
              <label
                htmlFor="partner-upload-input"
                className="btn-primary-elite"
                style={{
                  background: "#ec4899",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span>➕</span> Register New Asset
              </label>
            </div>
          </div>

          {selectedFile && (
            <div
              className="admin-form-elite"
              style={{
                marginBottom: "40px",
                border: "2px dashed #ec4899",
                background: "#fffcfd",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "30px" }}
              >
                <img
                  src={previewUrl}
                  alt="preview"
                  style={{
                    width: "120px",
                    height: "120px",
                    objectFit: "contain",
                    background: "#fff",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                />
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: 0, fontWeight: 800 }}>
                    Asset Initialization Fragment
                  </h4>
                  <p style={{ color: "#64748b", fontSize: "0.9rem" }}>
                    Ready for global deployment to partner matrix.
                  </p>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      className="btn-primary-elite"
                      style={{ background: "#ec4899" }}
                      onClick={saveImageToDatabase}
                      disabled={loading}
                    >
                      {loading ? "INITIALIZING..." : "CONFIRM DEPLOYMENT"}
                    </button>
                    <button
                      className="btn-update"
                      style={{ background: "#f1f5f9", border: "none" }}
                      onClick={() => {
                        setSelectedFile(null);
                        setPreviewUrl(null);
                      }}
                    >
                      ABORT
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div
            className="admin-gallery-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: "24px",
            }}
          >
            {uploadedImages.length === 0 ? (
              <div
                style={{
                  gridColumn: "1/-1",
                  textAlign: "center",
                  padding: "100px",
                  color: "#94a3b8",
                }}
              >
                No stakeholder fragments detected in the current buffer.
              </div>
            ) : (
              uploadedImages.map((image) => (
                <div
                  key={image._id}
                  className="admin-data-item"
                  style={{
                    flexDirection: "column",
                    position: "relative",
                    padding: "15px",
                    transition: "all 0.3s ease",
                  }}
                >
                  <img
                    src={`${API_BASE_URL}${image.url}`}
                    alt="partner"
                    style={{
                      width: "100%",
                      height: "140px",
                      objectFit: "contain",
                      background: "#f8fafc",
                      borderRadius: "12px",
                      padding: "10px",
                    }}
                  />
                  <div
                    style={{
                      marginTop: "15px",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <button
                      className="action-btn-elite btn-delete"
                      onClick={() => deleteImage(image._id)}
                      style={{ width: "100%", borderRadius: "8px" }}
                    >
                      <Trash2 size={16} style={{ marginRight: "8px" }} /> PURGE
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageUploadManager;
